import { idbKeyValGet, idbKeyValSet } from 'utils/idb';
import { useMountEffect } from '@tock/hooks';
import { useState } from 'react';

interface UseIDBKeyValStoreResult<T> {
  saveValue(value: T): void;
  value: T;
}

/**
 * A hook that reads and writes to an IndexedDB Key Value store.
 * @param key the key name for the store.
 * @param defaultValue the default value for the state if no value present (handy if you dont like undefined default).
 * @param onInitialize called after initial read is performed on mount.
 * @returns {Object} the saved value and a method to update the value.
 */
export function useIDBKeyValStore<T>(
  key: string | (() => string),
  defaultValue: T,
  onInitialize?: (value: T, time: number) => void
): UseIDBKeyValStoreResult<T> {
  const [value, setValue] = useState<T>(defaultValue);

  const getKeyValue = () => (typeof key === 'string' ? key : key());

  const saveValue = (value: T) => {
    const keyValue = getKeyValue();
    idbKeyValSet(keyValue, value);
    setValue(value);
  };

  useMountEffect(() => {
    const keyValue = getKeyValue();
    idbKeyValGet<T>(keyValue, defaultValue).then(({ time, value }) => {
      setValue(value);
      if (onInitialize) {
        onInitialize(value, time);
      }
    });
  });

  return { saveValue, value };
}
